import config from "react-global-configuration"

import { PATH } from "models/survey/_paths"
import { FOR_PATIENT } from "models/survey/constants"
import { Customer, DoctorType } from "types/entity"
import { LatestAnamnesis, ResponseAPI } from "types/payload"

import { Answer, Question, Value } from "../types"

export interface LatestAnamnesisCache {
  customerId: number | string
  patientId: number
  category: DoctorType
  data: LatestAnamnesis
}

export const LATEST_ANAMNESIS_CATEGORY = DoctorType.ORTHOPTIST

export const customerIdentity = (
  customer?: Partial<Customer>
): number | string | undefined =>
  customer ? customer.guid ?? customer.id : undefined

const REQUEST_TIMEOUT_MS = 10000

const LINE_FIELDS: Array<[PATH, keyof LatestAnamnesis]> = [
  [PATH.illness, "illness"],
  [PATH.optic_illness, "ophtalmologic_background"],
]

const DETAILS_FIELDS: Partial<
  Record<PATH, [PATH, keyof LatestAnamnesis, keyof LatestAnamnesis]>
> = {
  [PATH.illness_details]: [PATH.illness, "illness", "illness_details"],
  [PATH.optic_illness_details]: [
    PATH.optic_illness,
    "ophtalmologic_background",
    "ophtalmologic_background_details",
  ],
}

const CHOICE_FIELDS: Partial<
  Record<PATH, [PATH, keyof LatestAnamnesis, keyof LatestAnamnesis]>
> = {
  [PATH.contact_lens_type]: [PATH.reason, "reason", "contact_lens_type"],
  [PATH.contact_lens_brand]: [
    PATH.contact_lens_type,
    "contact_lens_type",
    "contact_lens_brand",
  ],
  [PATH.contact_lens_bending_radius]: [
    PATH.contact_lens_brand,
    "contact_lens_brand",
    "contact_lens_bending_radius",
  ],
  [PATH.contact_lens_diameter]: [
    PATH.contact_lens_bending_radius,
    "contact_lens_bending_radius",
    "contact_lens_diameter",
  ],
}

export const PREFILLABLE_QUESTION_IDS: string[] = [
  ...LINE_FIELDS.map(([id]) => id),
  ...Object.keys(DETAILS_FIELDS),
  ...Object.keys(CHOICE_FIELDS),
]

export const getLatestAnamnesisPatientId = (
  selectedPatientId: Value
): number | undefined =>
  typeof selectedPatientId === "number" && selectedPatientId > 0
    ? selectedPatientId
    : undefined

// Outside of request() on purpose: its global error handling (purge on 403,
// logout on 401, reload on timeout) must not be triggered by an optional prefill
export const fetchLatestAnamnesis = async (
  category: DoctorType,
  patientId: number
): Promise<LatestAnamnesis | null> => {
  const params = new URLSearchParams({ category: String(category) })
  if (patientId !== FOR_PATIENT) params.set("relative_id", String(patientId))
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const response = await fetch(
      `/api${config.get("patient.get.latest_anamnesis")}?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "X-User-Token": sessionStorage.getItem("X-USER-Token") || "",
        },
        signal: controller.signal,
      }
    )
    if (!response.ok) {
      throw new Error(`Latest anamnesis request failed (${response.status})`)
    }
    const body: ResponseAPI<LatestAnamnesis | null> = await response.json()
    return body?.data ?? null
  } finally {
    clearTimeout(timeout)
  }
}

const findAnswer = (answers: Answer[], questionId: string): Answer | undefined =>
  answers.find((answer) => answer.questionId === questionId)

const isConsumed = (consumed: string[] | undefined, questionId: string) =>
  !!consumed?.includes(questionId)

export const buildAnamnesisPrefill = (
  question: Question | undefined,
  latest: LatestAnamnesis | null | undefined,
  answers: Answer[],
  consumed?: string[]
): Answer[] => {
  if (!question || !latest) return []

  if (question.id === PATH.anamnesis_questions) {
    return LINE_FIELDS.filter(
      ([lineId, field]) =>
        typeof latest[field] === "boolean" &&
        !findAnswer(answers, lineId) &&
        !isConsumed(consumed, lineId)
    ).map(([lineId, field]) => ({ questionId: lineId, value: [latest[field]] }))
  }

  if (findAnswer(answers, question.id) || isConsumed(consumed, question.id))
    return []
  const allowed = (question.replies || []).map((reply) => reply.value)

  const details = DETAILS_FIELDS[question.id]
  if (details) {
    const [lineId, booleanField, detailsField] = details
    const current = findAnswer(answers, lineId)?.value?.[0]
    if (current !== true || latest[booleanField] !== true) return []
    const value = String(latest[detailsField] || "")
      .split(",")
      .map((detail) => detail.trim())
      .filter(
        (detail, index, all) =>
          detail && allowed.includes(detail) && all.indexOf(detail) === index
      )
    return value.length ? [{ questionId: question.id, value }] : []
  }

  const choice = CHOICE_FIELDS[question.id]
  if (!choice) return []
  const [parentId, parentField, field] = choice
  const value = latest[field]
  if (
    value === null ||
    value === undefined ||
    findAnswer(answers, parentId)?.value?.[0] !== latest[parentField] ||
    (allowed.length ? !allowed.includes(value) : typeof value !== "number")
  )
    return []
  return [{ questionId: question.id, value: [value] }]
}

export const mergePrefilledAnswers = (
  answers: Answer[],
  prefilled: Answer[]
): Answer[] => [
  ...answers,
  ...prefilled.filter((answer) => !findAnswer(answers, answer.questionId)),
]
